import { Shimmer } from "../../../../components/common/shimmer";

/**
 * Shimmer skeleton for the products compare page. Mirrors the final layout
 * (breadcrumbs → header + actions → comparison table → recommendation cards)
 * so the page doesn't visually jump once the variants finish loading.
 */
interface ComparePageSkeletonProps {
  /** Number of product columns to show. Defaults to 3 — matches the typical
   * compare-3 view; falls back if the query string carries a different count. */
  productCount?: number;
}

export function ComparePageSkeleton({ productCount = 3 }: ComparePageSkeletonProps) {
  const cols = Math.max(2, Math.min(4, productCount));
  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6">
      {/* Breadcrumbs */}
      <SkelLine className="h-3 w-72" />

      {/* Header: heading + actions */}
      <div className="flex items-center justify-between gap-4">
        <SkelLine className="h-7 w-72" />
        <div className="flex items-center gap-3">
          <SkelPill className="h-9 w-40" />
          <SkelPill className="h-9 w-44" />
          <SkelPill className="h-9 w-36" />
        </div>
      </div>

      {/* Comparison table */}
      <div className="overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite">
        {/* Header row: image + name per column */}
        <div className="flex">
          <div className="w-[260px] shrink-0 bg-bgMuted py-5 pl-6 pr-4">
            <SkelLine className="h-3 w-20" />
          </div>
          {Array.from({ length: cols }).map((_, i) => (
            <div
              key={`head-${i}`}
              className="flex flex-1 flex-col items-center gap-3 py-5 px-2"
            >
              <SkelLine className="h-32 w-full max-w-[180px] rounded-lg" />
              <SkelLine className="h-3.5 w-3/4" />
              <SkelLine className="h-3 w-1/2" />
            </div>
          ))}
        </div>
        <RowDivider />

        {/* Price row */}
        <div className="flex">
          <LabelCellSkel label="w-16" />
          {Array.from({ length: cols }).map((_, i) => (
            <div
              key={`price-${i}`}
              className="flex flex-1 flex-col items-center gap-1.5 py-5 px-2"
            >
              <SkelLine className="h-3 w-16" />
              <SkelLine className="h-5 w-24" />
              <SkelLine className="h-2.5 w-32" />
            </div>
          ))}
        </div>
        <RowDivider />

        {/* Spec rows */}
        {Array.from({ length: 8 }).map((_, r) => (
          <div key={`row-${r}`}>
            <div className="flex">
              <LabelCellSkel
                label={r % 2 === 0 ? "w-24" : "w-32"}
              />
              {Array.from({ length: cols }).map((_, i) => (
                <div
                  key={`cell-${r}-${i}`}
                  className="flex flex-1 items-center justify-center py-4 px-2"
                >
                  <SkelLine
                    className={`h-3 ${
                      (r + i) % 3 === 0
                        ? "w-24"
                        : (r + i) % 3 === 1
                          ? "w-16"
                          : "w-20"
                    }`}
                  />
                </div>
              ))}
            </div>
            <RowDivider />
          </div>
        ))}

        {/* Action row: button per column */}
        <div className="flex">
          <div className="w-[260px] shrink-0 bg-bgMuted py-4 pl-6 pr-4" />
          {Array.from({ length: cols }).map((_, i) => (
            <div
              key={`act-${i}`}
              className="flex flex-1 items-center justify-center py-4 px-2"
            >
              <SkelPill className="h-9 w-32" />
            </div>
          ))}
        </div>
      </div>

      {/* Rule-based recommendation card */}
      <RecommendationCardSkel />

      {/* AI recommendation card */}
      <RecommendationCardSkel />
    </div>
  );
}

function SkelLine({ className = "" }: { className?: string }) {
  return <Shimmer className={`rounded-md bg-bgLight ${className}`} />;
}

function SkelPill({ className = "" }: { className?: string }) {
  return <Shimmer className={`rounded-full bg-bgLight ${className}`} />;
}

function RowDivider() {
  return <div className="h-px bg-borderDefault" />;
}

function LabelCellSkel({ label }: { label: string }) {
  return (
    <div className="w-[260px] shrink-0 bg-bgMuted py-4 pl-6 pr-4">
      <SkelLine className={`h-3 ${label}`} />
    </div>
  );
}

function RecommendationCardSkel() {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <SkelLine className="h-5 w-56" />
      <SkelLine className="h-3 w-full" />
      <SkelLine className="h-3 w-11/12" />
      <SkelLine className="h-3 w-3/4" />
    </div>
  );
}

export default ComparePageSkeleton;
